import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Width in px of the left-edge zone where a swipe means "back". Horizontal
 * swipers (tab content) must ignore touches starting inside it, or one gesture
 * both goes back and switches the tab.
 */
export const SWIPE_BACK_EDGE_WIDTH = 30;

interface SwipeBackOptions {
  /** Edge zone width in pixels where swipe is detected (default: SWIPE_BACK_EDGE_WIDTH) */
  edgeWidth?: number;
  /** Minimum swipe distance to trigger back (default: 80) */
  threshold?: number;
  /** Whether to enable the hook (default: true) */
  enabled?: boolean;
  /**
   * Custom handler invoked when a back-swipe is detected. If omitted, falls back to `navigate(-1)`.
   * Use this to mirror the Header back-button's semantic navigation (e.g. match → tournament → list)
   * instead of raw browser history.
   */
  onSwipeBack?: () => void;
}

/**
 * Hook that triggers back navigation when the user swipes from the left edge of the screen
 * to the right. Similar to iOS Safari swipe-back gesture.
 */
export const useSwipeBack = (options: SwipeBackOptions = {}) => {
  const { edgeWidth = SWIPE_BACK_EDGE_WIDTH, threshold = 80, enabled = true, onSwipeBack } = options;
  const navigate = useNavigate();

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isEdgeSwipe = useRef(false);

  // Stable ref so the touchend listener always sees the latest handler without re-attaching.
  const onSwipeBackRef = useRef(onSwipeBack);
  onSwipeBackRef.current = onSwipeBack;

  useEffect(() => {
    if (!enabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      // Check if touch started from left edge
      isEdgeSwipe.current = touch.clientX <= edgeWidth;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isEdgeSwipe.current || touchStartX.current === null || touchStartY.current === null) {
        return;
      }

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = Math.abs(touch.clientY - touchStartY.current);

      // Check if swipe was horizontal enough (not vertical scroll)
      // and traveled far enough to the right
      if (deltaX > threshold && deltaX > deltaY * 2) {
        const handler = onSwipeBackRef.current;
        if (handler) {
          handler();
        } else {
          navigate(-1);
        }
      }

      // Reset
      touchStartX.current = null;
      touchStartY.current = null;
      isEdgeSwipe.current = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, edgeWidth, threshold, navigate]);
};

export default useSwipeBack;
